import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { authService } from '../services/authService'
import { triggerAuthChange } from '../utils/authEvents'
import type { AuthState } from '../types/auth'

interface AuthContextType extends AuthState {
  login: (username: string, password: string, rememberMe?: boolean, tenantIdOverride?: string) => Promise<{ success: boolean; errorMessage?: string }>
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    refreshToken: null,
    isLoading: true, // Start with loading true
    error: null
  })

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth()
  }, [])

  // Listen for auth changes from other components
  useEffect(() => {
    const handleAuthChange = () => {
      refreshAuth()
    }

    window.addEventListener('auth-change', handleAuthChange)
    return () => {
      window.removeEventListener('auth-change', handleAuthChange)
    }
  }, [])

  const initializeAuth = async () => {
    try {
      // Auth initialization logging ถูกปิดเพื่อลด spam logs
      
      // Check if user is authenticated
      let isAuth = authService.isAuthenticated()
      let token = authService.getToken()
      let user = authService.getCurrentUser()
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken')
      
      // Auth check logging ถูกปิดเพื่อลด spam logs
      
      // If token is expired but we have refresh token, try to refresh
      if (!isAuth && refreshToken && user) {
        try {
          const refreshResult = await authService.refreshToken()
          if (refreshResult.success && refreshResult.data) {
            // Update local variables with new token
            token = refreshResult.data.token
            isAuth = authService.isAuthenticated()
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ Token refreshed successfully')
            }
          }
        } catch (refreshError) {
          console.error('❌ Token refresh error:', refreshError)
        }
      }
      
      if (isAuth && token && user) {
        setAuthState({
          isAuthenticated: true,
          user: user as any,
          token,
          refreshToken,
          isLoading: false,
          error: null
        })
        // User authenticated logging ถูกปิดเพื่อลด spam logs
      } else {
        // Clear any stale data from both storages
        localStorage.removeItem('token')
        localStorage.removeItem('refreshToken')
        localStorage.removeItem('user')
        localStorage.removeItem('tenantId')
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('refreshToken')
        sessionStorage.removeItem('user')
        
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
          isLoading: false,
          error: null
        })
        // User not authenticated (reduced logging)
        if (process.env.NODE_ENV === 'development') {
          console.log('ℹ️ User is not authenticated')
        }
      }
    } catch (error) {
      console.error('❌ Error initializing auth:', error)
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        isLoading: false,
        error: 'Failed to initialize authentication'
      })
    }
  }

  const login = async (
    username: string,
    password: string,
    rememberMe = false,
    tenantIdOverride?: string
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }))
    
    try {
      console.log('🔐 AuthContext login attempt...', { username, rememberMe, tenantIdOverride })
      const tenantId = tenantIdOverride || await authService.resolveTenantId()
      
      const response = await authService.login({
        username,
        password,
        tenantId,
        rememberMe
      })
      
      if (response.success && response.data) {
        setAuthState({
          isAuthenticated: true,
          user: response.data.user as any,
          token: response.data.tokens?.accessToken || null,
          refreshToken: response.data.tokens?.refreshToken || null,
          isLoading: false,
          error: null
        })
        
        // Save username if rememberMe is enabled
        if (rememberMe) {
          localStorage.setItem('savedUsername', username)
        } else {
          localStorage.removeItem('savedUsername')
        }
        
        console.log('✅ AuthContext login successful')
        // Reconnect WebSocket with JWT so websocket-gateway emits user_online (admin/users sees online)
        triggerAuthChange()
        return { success: true }
      } else {
        // Check if it's a concurrent login error from response
        if ((response as any).code === 'CONCURRENT_LOGIN_BLOCKED') {
          setAuthState(prev => ({
            ...prev,
            isLoading: false,
            error: 'CONCURRENT_LOGIN_BLOCKED'
          }))
          
          console.log('🔄 AuthContext concurrent login detected from response')
          return { success: false, errorMessage: 'CONCURRENT_LOGIN_BLOCKED' }
        }
        
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: response.message || 'Login failed'
        }))
        
        console.error('❌ AuthContext login failed:', response.message)
        return { success: false, errorMessage: response.message || 'Login failed' }
      }
    } catch (error: any) {
      let errorMessage = error instanceof Error ? error.message : 'Login error'
      
      // Check if it's a concurrent login error
      if (error.status === 409 && error.response?.code === 'CONCURRENT_LOGIN_BLOCKED') {
        setAuthState(prev => ({
          ...prev,
          isLoading: false,
          error: 'CONCURRENT_LOGIN_BLOCKED'
        }))
        
        console.log('🔄 AuthContext concurrent login detected, returning special error')
        return { success: false, errorMessage: 'CONCURRENT_LOGIN_BLOCKED' }
      }

      if (error.status === 401) {
        errorMessage = 'ชื่อผู้ใช้งานหรือรหัสผ่านไม่ถูกต้อง'
      } else if (error.status === 423) {
        errorMessage = 'บัญชีถูกระงับชั่วคราว เนื่องจากพยายามเข้าสู่ระบบผิดหลายครั้ง'
      } else if (error.status === 403) {
        errorMessage = error.response?.message || 'บัญชีไม่สามารถใช้งานได้'
      } else if (error.response?.message) {
        errorMessage = error.response.message
      }
      
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }))
      
      console.error('❌ AuthContext login error:', error)
      return { success: false, errorMessage: errorMessage }
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authService.logout()
      
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        isLoading: false,
        error: null
      })
      
      // Clear any concurrent login flags
      sessionStorage.removeItem('concurrentLoginDetected')
      
    } catch (error) {
      console.error('❌ Logout error:', error)
      
      // Still update state even if API call fails
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        isLoading: false,
        error: null
      })
      
      // Clear any concurrent login flags
      sessionStorage.removeItem('concurrentLoginDetected')
      
      // Re-throw error so calling component can handle it
      throw error
    }
  }

  const refreshAuth = async (): Promise<void> => {
    try {
      // Auth refresh logging ถูกปิดเพื่อลด spam logs
      
      // Check current auth status
      const isAuth = authService.isAuthenticated()
      const token = authService.getToken()
      const user = authService.getCurrentUser()
      const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken')
      
      if (isAuth && token && user) {
        setAuthState({
          isAuthenticated: true,
          user: user as any,
          token,
          refreshToken,
          isLoading: false,
          error: null
        })
        // Auth state refreshed logging ถูกปิดเพื่อลด spam logs
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          token: null,
          refreshToken: null,
          isLoading: false,
          error: null
        })
        // Reduced logging for not authenticated
      }
    } catch (error) {
      console.error('❌ Error refreshing auth state:', error)
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to refresh authentication'
      }))
    }
  }

  const clearError = (): void => {
    setAuthState(prev => ({ ...prev, error: null }))
  }

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshAuth,
    clearError
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
